import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { Button, Spinner, SectionHeader } from '../ui';
import { NewProjectModal } from './NewProjectModal';
import { BoardColumn } from './BoardColumn';
import { BoardCard } from './BoardCard';
import { BoardCardDetail } from './BoardCardDetail';
import { usePipelineBoard } from '../../hooks/usePipelineBoard';
import { GTMProject, PIPELINE_STAGES, PipelineBoard as Board, PipelineStage } from '../../types/pipeline';
import { PipelineFilters, PipelineFilterState, PipelineViewMode } from './PipelineFilters';
import { PipelineTable } from './PipelineTable';

function findProject(board: Board, id: string): GTMProject | undefined {
  for (const stage of PIPELINE_STAGES) {
    const found = board[stage].find(p => p.id === id);
    if (found) return found;
  }
  return undefined;
}

export const PipelineBoard: React.FC = () => {
  const { board, isLoading, error, refetch, moveProject } = usePipelineBoard();
  const [selected, setSelected] = useState<GTMProject | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [filters, setFilters] = useState<PipelineFilterState>({
    search: '',
    client: '',
    stage: '',
    needsHumanOnly: false,
  });
  const [view, setView] = useState<PipelineViewMode>(() => {
    try {
      return (localStorage.getItem('skylarq:pipeline-view') as PipelineViewMode) || 'board';
    } catch {
      return 'board';
    }
  });
  const changeView = (v: PipelineViewMode) => {
    setView(v);
    try {
      localStorage.setItem('skylarq:pipeline-view', v);
    } catch {
      /* localStorage unavailable */
    }
  };

  // 8px activation distance so a tap opens the detail panel instead of starting a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  if (isLoading && !board) {
    return <Spinner size="md" text="Loading pipeline…" center />;
  }

  if (!board) {
    return (
      <div className="p-6 text-sm text-neutral-500">{error || 'Pipeline board is unavailable.'}</div>
    );
  }

  const flagged = PIPELINE_STAGES.flatMap(stage => board[stage]).filter(p => p.needs_human);
  const activeProject = activeId ? findProject(board, activeId) : null;

  const matches = (p: GTMProject) =>
    (!filters.search || p.name.toLowerCase().includes(filters.search.toLowerCase())) &&
    (!filters.client || p.client_entity_name === filters.client) &&
    (!filters.stage || p.pipeline_stage === filters.stage) &&
    (!filters.needsHumanOnly || p.needs_human);

  const allProjects = PIPELINE_STAGES.flatMap((stage) => board[stage]);
  const clients = Array.from(
    new Set(allProjects.map((p) => p.client_entity_name).filter((c): c is string => !!c))
  ).sort();
  const filteredProjects = allProjects.filter(matches);
  // Filtered board for the kanban (drag still works on the visible subset).
  const filteredBoard = Object.fromEntries(
    PIPELINE_STAGES.map((stage) => [stage, board[stage].filter(matches)])
  ) as Board;

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const project = findProject(board, String(active.id));
    const toStage = String(over.id) as PipelineStage;
    if (project && project.pipeline_stage !== toStage) {
      moveProject(project.id, toStage);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <SectionHeader
        level={1}
        action={
          <Button variant="primary" size="sm" onClick={() => setIsCreateOpen(true)}>
            New project
          </Button>
        }
      >
        Pipeline
      </SectionHeader>

      <PipelineFilters
        filters={filters}
        onChange={setFilters}
        view={view}
        onViewChange={changeView}
        clients={clients}
      />

      {flagged.length > 0 && (
        <div className="mt-4 rounded-md border border-warning-500/30 bg-warning-500/5 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4 text-warning-500" />
            <span className="text-13 font-semibold text-neutral-700">Needs you</span>
            <span className="text-xs text-neutral-400">{flagged.length}</span>
          </div>
          <div className="flex flex-col md:flex-row md:flex-wrap gap-2">
            {flagged.map(p => (
              <div key={p.id} className="md:w-72">
                <BoardCard project={p} onClick={setSelected} showStage />
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'board' ? (
        <DndContext
          sensors={sensors}
          onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="mt-4 flex flex-col md:flex-row gap-3 md:overflow-x-auto md:pb-2">
            {PIPELINE_STAGES.map(stage => (
              <BoardColumn
                key={stage}
                stage={stage}
                projects={filteredBoard[stage]}
                onCardClick={setSelected}
              />
            ))}
          </div>
          <DragOverlay>
            {activeProject ? (
              <div className="md:w-72">
                <BoardCard project={activeProject} onClick={() => {}} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <PipelineTable projects={filteredProjects} onRowClick={setSelected} />
      )}

      <NewProjectModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={refetch}
      />

      {selected && (
        <BoardCardDetail
          project={selected}
          onClose={() => setSelected(null)}
          onChanged={refetch}
        />
      )}
    </div>
  );
};
